import { Routes, Route, Outlet, Link, BrowserRouter, Navigate } from "react-router-dom";
import Home from "./pages/Home";
import Registration from "./pages/Registration";
import MyTemplates from "./pages/MyTemplates";
import MySubmissionsPage from "./pages/MySubmissions";
import AllSubmissionsPage from "./pages/AllSubmissions";
import EditForm from "./pages/EditForm/booking";
import ChangePassword from "./pages/ChangePassword";
import MassSubmitPage from "./pages/MassSubmit";
import SectionCodesList from "./pages/SectionCodes/List";
import SectionCodeDetail from "./pages/SectionCodes/Detail";
import TiaDataManagement from "./pages/TiaDataManagement";
import Logo from './images/logo.png';
import { AuthContext } from './AuthContext';
import { useEffect, useState } from "react";
import Login from "./pages/Login";
import { Row, Col, Button, message } from 'antd';
import Accounts from "./pages/Accounts";
import { getUserInfo } from "./api";

const API_URL = process.env.REACT_APP_API_URL;

export default function App() {
    const [loggedIn, setLoggedIn] = useState(() => {
        const savedLoggedIn = localStorage.getItem('loggedIn');
        return savedLoggedIn ? JSON.parse(savedLoggedIn) : false;
    });
    const [userRole, setUserRole] = useState<number | null>(null);

    useEffect(() => {
        localStorage.setItem('loggedIn', JSON.stringify(loggedIn));
        if (loggedIn) {
            // Fetch user info to get role
            const fetchUserRole = async () => {
                try {
                    const response = await getUserInfo();
                    if (response.status === 200) {
                        const result = await response.json();
                        if (result.success) {
                            setUserRole(result.data.role);
                        }
                    }
                } catch (err) {
                    console.log('Failed to fetch user info');
                }
            };
            
            fetchUserRole();

            const refreshSession = async () => {
                try {
                    await fetch(`${API_URL}/refresh-session`, {
                        method: 'POST',
                        credentials: 'include', 
                    });
                } catch (err) {
                    console.log('Failed to refresh session');
                }
            };
        
            refreshSession();
            const intervalId = setInterval(refreshSession, 12 * 60 * 60 * 1000); // 12 hours
            return () => clearInterval(intervalId);
        }
    }, [loggedIn]);

    return (
        <AuthContext.Provider value={{ loggedIn, setLoggedIn }}>
            <Routes>
                <Route path="/register" element={<Registration />} />
                <Route path="/login" element={loggedIn ? <Navigate to="/" /> : <Login />} />
                <Route path="/" element={!loggedIn ? <Navigate to="/login" /> : <Layout userRole={userRole} />} >
                    <Route index element={
                        userRole !== null && userRole >= 2 
                            ? <Navigate to="/all-submissions" replace /> 
                            : <Navigate to="/my-templates" replace />
                    } />
                    <Route path="my-templates" element={
                        userRole !== null && userRole >= 2 
                            ? <Navigate to="/all-submissions" replace /> 
                            : <MyTemplates />
                    } />
                    <Route path="my-submissions" element={
                        userRole !== null && userRole >= 2 
                            ? <Navigate to="/all-submissions" replace /> 
                            : <MySubmissionsPage />
                    } />
                    <Route path="all-submissions" element={
                        userRole !== null && userRole >= 2 
                            ? <AllSubmissionsPage /> 
                            : <Navigate to="/my-submissions" replace />
                    } />
                    <Route path="accounts" element={
                        userRole !== null && userRole >= 2 ? <Accounts /> : <Navigate to="/" />
                    } />
                    <Route path="tia-data" element={
                        userRole !== null && userRole >= 2 ? <TiaDataManagement /> : <Navigate to="/" />
                    } />
                    <Route path="change-password" element={<ChangePassword />} />
                    <Route path="new-form" element={<EditForm />} />
                    <Route path="template/:id" element={<EditForm />} />
                    <Route path="form/:recordStr" element={<EditForm />} />
                    <Route path="mass-submit" element={<MassSubmitPage />} />
                    <Route path="section-codes" element={<SectionCodesList />} />
                    <Route path="section-codes/new" element={<SectionCodeDetail />} />
                    <Route path="section-codes/:code" element={<SectionCodeDetail />} />
                    <Route path="*" element={<NoMatch />} />
                </Route>
            </Routes>
        </AuthContext.Provider>
    );
}

async function handleLogout() {
    const reponse = await fetch(`${API_URL}/logout`, {
        method: 'POST',
        credentials: 'include',
    });
    if (reponse.status === 200) {
        localStorage.removeItem('loggedIn');
        window.location.href = '/login';
    }
    else {
        console.log('Failed to logout');
    }
}

function Layout({ userRole }: { userRole: number | null }) {
    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
            <nav className="navbar navbar-expand-lg fixed-top navbar-dark navbarScroll">
                <div className="navbar-logo" style={{ marginRight: '12px' }}>
                    <img src={Logo} alt="Logo" />
                </div>
                <h1 style={{ 
                    color: '#ffffff', 
                    fontSize: '24px', 
                    fontWeight: '700',
                    margin: 0,
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                }}>Tour Fast 報團快</h1>
                <div className="collapse navbar-collapse" id="navbarSupportedContent">
                    <Row justify="space-between" align="middle" style={{ width: '100%' }}>
                        <Col>
                            <ul className="navbar-nav ms-auto">
                                {userRole !== null && userRole >= 2 ? (
                                    // Admin/SuperAdmin menu
                                    <>
                                        <li className="nav-item">
                                            <Link className="nav-link" to="/all-submissions">所有提交紀錄</Link>
                                        </li>
                                        <li className="nav-item">
                                            <Link className="nav-link" to="/accounts">用戶管理</Link>
                                        </li>
                                        <li className="nav-item">
                                            <Link className="nav-link" to="/tia-data">資料管理</Link>
                                        </li>
                                        <li className="nav-item">
                                            <Link className="nav-link" to="/section-codes">區段代碼</Link>
                                        </li>
                                    </>
                                ) : (
                                    // Regular user menu
                                    <>
                                        <li className="nav-item">
                                            <Link className="nav-link" to="/my-templates">我的範本</Link>
                                        </li>
                                        <li className="nav-item">
                                            <Link className="nav-link" to="/my-submissions">提交紀錄</Link>
                                        </li>
                                        {/* <li className="nav-item">
                                            <Link className="nav-link" to="/mass-submit">批次提交</Link>
                                        </li>
                                        <li className="nav-item">
                                            <Link className="nav-link" to="/section-codes">區段代碼</Link>
                                        </li> */}
                                    </>
                                )}
                                <li className="nav-item">
                                    <Link className="nav-link" to="/change-password">更改密碼</Link>
                                </li>
                            </ul>
                        </Col>
                        <Col>
                            <Button 
                                type="primary" 
                                onClick={handleLogout}
                                style={{ 
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                    color: '#ffffff',
                                    fontWeight: '500',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                                }}
                            >
                                登出
                            </Button>
                        </Col>
                    </Row>
                </div>
            </nav>
            <div style={{ paddingTop: '10px' }}>
                <Outlet />
            </div>
        </div>
    );
}

function NoMatch() {
    return (
        <div>
            <h2>Nothing to see here!</h2>
            <p>
                <Link to="/">Go to the home page</Link>
            </p>
        </div>
    );
}
